import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { motion } from "framer-motion";
import { WIDTH } from "../features/foundation";

const COLOR_TOOLTIP = {
    black: "bg-black",
    white: "bg-white",
    darkgray: "bg-darkgray",
    gray: "bg-gray",
    lightgray: "bg-lightgray",
    blue: "bg-blue",
    success: "bg-success",
    error: "bg-error",
    info: "bg-info",
    warning: "bg-warning",
};

type TooltipProps = {
    children?: React.ReactNode;
    content?: React.ReactNode;
    enabled?: boolean;
    mode?: "default" | "always";
    size?: keyof typeof WIDTH;
    color?: keyof typeof COLOR_TOOLTIP;
};

type TooltipContentProps = {
    $: any;
    mode?: "default" | "always";
    content?: React.ReactNode;
    color?: keyof typeof COLOR_TOOLTIP;
};

const TooltipContent = ({ $, mode, content, color = "error" }: TooltipContentProps) => {
    const [pos, setPos] = useState<any>(null);
    $.current.setPos = setPos;

    return (
        <motion.div
            className={classNames(
                "z-[9999] flex px-2 py-1 rounded shadow justify-center tooltip",
                COLOR_TOOLTIP[color],
                mode === "always" && "absolute bottom-full left-1/2 -translate-x-1/2 -translate-y-2",
                mode === "default" && "fixed",
            )}
            style={{
                ...(mode === "default" &&
                    pos && {
                        top: pos.y,
                        left: pos.x + pos.width / 2,
                        transform: "translate(-50%, calc(-100% - 8px))",
                    }),
            }}
            initial={{ opacity: mode === "always" ? 1 : 0, pointerEvents: "none" }}
            variants={{ hover: { opacity: 1, pointerEvents: "all" } }}
        >
            <div className={"absolute w-2 h-2 top-full -translate-y-1 rotate-45 " + COLOR_TOOLTIP[color]} />
            <div className="min-w-max text-white">
                {typeof content === "string"
                    ? String(content)
                          .split("\n")
                          .map((line, index) => {
                              return (
                                  <span key={"$tooltip$" + index}>
                                      {line}
                                      <br />
                                  </span>
                              );
                          })
                    : content}
            </div>
            <div className="absolute w-full top-full h-2 left-1/2 -translate-x-1/2" />
        </motion.div>
    );
};

export const Tooltip = (props: TooltipProps) => {
    const { children, content, mode = "default", enabled = true, size = "fit", color = "error" } = props;

    const $ = useRef<any>({ setPos: null });

    return (
        <motion.div
            className={classNames("relative", WIDTH[size])}
            whileHover="hover"
            onMouseOver={(event) => {
                event.stopPropagation();
                if ($.current.setPos && mode === "default") {
                    console.log(event.currentTarget);
                    $.current.setPos(event.currentTarget.getBoundingClientRect());
                }
            }}
        >
            {children}
            {enabled &&
                content &&
                (mode === "always" ? (
                    <TooltipContent $={$} mode={mode} content={content} color={color} />
                ) : (
                    createPortal(<TooltipContent $={$} mode={mode} content={content} color={color} />, document.body)
                ))}
        </motion.div>
    );
};
